import { Icon } from '@mdvip/shared/components/blocks/Icon';
import {
  ResponsiveModal,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from '@mdvip/shared/components/blocks/ResponsiveModal';
import { Button } from '@mdvip/shared/components/ui/button';
import { Checkbox } from '@mdvip/shared/components/ui/checkbox';
import { Label } from '@mdvip/shared/components/ui/label';
import { useCurrency } from '@mdvip/shared/hooks/useCurrency';
import { cn } from '@mdvip/shared/lib/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSavedCreditCards } from '@/hooks/payments/creditCard/useSavedCreditCards';
import { useEnableMyAutoPay } from '@/hooks/payments/memberInfo/useEnableMyAutoPay';
import { useMemberAutoPaySummary } from '@/hooks/payments/memberInfo/useMemberAutoPaySummary';
import type { CreditCardAccount } from '@/lib/payments/creditCardAccount.types';
import { type PaymentAccountItem, PickerPaymentAccount } from './PickerPaymentAccount';

interface AutoPayEnrollmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-selects a matching saved account when the modal opens (e.g. after adding one). */
  defaultProfileId?: string;
  /** Opens the "add payment account" flow from the picker / empty state. Omit to hide the affordance. */
  onAddAccount?: () => void;
}

/** Saved card → picker item. Label matches the design: "VISA ···0009 · Exp 05/2029". */
function toItem(card: CreditCardAccount): PaymentAccountItem {
  const expiry = `${String(card.expirationMonth).padStart(2, '0')}/${card.expirationYear}`;
  return {
    id: card.id,
    label: `${card.network} ···${card.last4} · Exp ${expiry}`,
    isDefault: card.isDefault,
  };
}

function SummaryGrid({ amount, plan, method }: { amount: number | null; plan: string | null; method?: string }) {
  const { t } = useTranslation('members');
  const { formatCurrency } = useCurrency();
  return (
    <div className="w-full rounded-xl border bg-muted/30 text-left">
      <div className="flex justify-between border-b px-4 py-2.5 text-sm">
        <span className="text-muted-foreground">{t('payments.autopay.modal.amount')}</span>
        <span className="font-semibold tabular-nums">{amount === null ? '—' : formatCurrency(amount)}</span>
      </div>
      <div className={cn('flex justify-between px-4 py-2.5 text-sm', method && 'border-b')}>
        <span className="text-muted-foreground">{t('payments.autopay.modal.paymentPlan')}</span>
        <span className="font-semibold">{plan ?? '—'}</span>
      </div>
      {method && (
        <div className="flex justify-between px-4 py-2.5 text-sm">
          <span className="text-muted-foreground">{t('payments.autopay.modal.method')}</span>
          <span className="ml-4 text-right font-semibold">{method}</span>
        </div>
      )}
    </div>
  );
}

/**
 * AutoPayEnrollmentModal - enroll the member in AutoPay: pick the account, accept the required
 * authorization, confirm. Folds the enrollment form and the "AutoPay is ON" success into one modal
 * (the responsive `ResponsiveModal` shell). Confirm stays enabled - a missing authorization or an
 * expired selection surface on click; only the in-flight state disables it. Zero saved accounts
 * shows an empty state; a member who is somehow already enrolled sees an informational message.
 */
export function AutoPayEnrollmentModal({
  open,
  onOpenChange,
  defaultProfileId,
  onAddAccount,
}: AutoPayEnrollmentModalProps) {
  const { t } = useTranslation('members');
  const { data: summary } = useMemberAutoPaySummary();
  const { data: cards = [], isLoading: isLoadingCards } = useSavedCreditCards();
  const { enroll, isPending } = useEnableMyAutoPay();

  const [step, setStep] = useState<'form' | 'success'>('form');
  const [selId, setSelId] = useState('');
  const [auth, setAuth] = useState(false);
  const [touched, setTouched] = useState(false);

  // Fresh form on every open (no stale selection/authorization leaking into the next enrollment).
  useEffect(() => {
    if (open) {
      setStep('form');
      setSelId('');
      setAuth(false);
      setTouched(false);
    }
  }, [open]);

  // Expired cards are hidden outright rather than shown-but-blocked, matching legacy. A member
  // whose only cards are expired therefore falls through to the "no payment methods" empty state,
  // which is the intended behavior.
  const accounts = cards.filter((card) => !card.isExpired).map(toItem);
  const hasAccounts = accounts.length > 0;
  const alreadyOn = summary?.autoPayEnrolled ?? false;
  const amount = summary?.autoPayAmount ?? null;
  const plan = summary?.autoPayPlan ?? null;

  // Derive the effective selection so it resolves once accounts load (rather than seeding an effect
  // that would fight the async query): the current pick if still valid, else the default-or-first.
  const resolvedId = accounts.some((a) => a.id === selId)
    ? selId
    : (defaultProfileId && accounts.some((a) => a.id === defaultProfileId) && defaultProfileId) ||
      (accounts.find((a) => a.isDefault) ?? accounts[0])?.id ||
      '';
  const sel = accounts.find((a) => a.id === resolvedId);
  const canConfirm = auth && hasAccounts && !isLoadingCards;

  const handleConfirm = async () => {
    setTouched(true);
    if (!canConfirm || isPending || !sel) return;
    try {
      // AutoPay always drafts the member's default card, so a non-default pick is promoted first.
      await enroll({ cardId: sel.id, makeDefault: !sel.isDefault });
      setStep('success');
    } catch {
      // Toast is raised in the hook; keep the modal open with Confirm re-enabled.
    }
  };

  if (step === 'success') {
    return (
      <ResponsiveModal open={open} onOpenChange={onOpenChange} className="sm:max-w-sm">
        <ResponsiveModalBody>
          <div className="flex min-h-full flex-col items-center justify-center text-center sm:min-h-0 sm:py-2">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-success/10 text-success">
              <Icon name="circleCheck" weight="solid" className="text-[28px]" />
            </div>
            <div className="text-xl font-light text-foreground">{t('payments.autopay.modal.successEyebrow')}</div>
            <ResponsiveModalTitle className="mb-4 text-3xl font-bold leading-tight text-foreground">
              {t('payments.autopay.modal.successTitle')}
            </ResponsiveModalTitle>
            <SummaryGrid amount={amount} plan={plan} method={sel?.label} />
          </div>
        </ResponsiveModalBody>
        <ResponsiveModalFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            {t('payments.autopay.modal.backToPaymentCenter')}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModal>
    );
  }

  if (alreadyOn) {
    return (
      <ResponsiveModal open={open} onOpenChange={onOpenChange} className="sm:max-w-sm">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>{t('payments.autopay.label')}</ResponsiveModalTitle>
        </ResponsiveModalHeader>
        <ResponsiveModalBody>
          <p className="text-sm text-muted-foreground">{t('payments.autopay.modal.alreadyOn')}</p>
        </ResponsiveModalBody>
        <ResponsiveModalFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            {t('payments.autopay.modal.backToPaymentCenter')}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModal>
    );
  }

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange} className="sm:max-w-lg">
      <ResponsiveModalHeader>
        <ResponsiveModalTitle>{t('payments.autopay.modal.title')}</ResponsiveModalTitle>
      </ResponsiveModalHeader>

      <ResponsiveModalBody>
        <div className="mb-4">
          <SummaryGrid amount={amount} plan={plan} />
        </div>

        <div className="mb-4">
          {isLoadingCards ? (
            // Same 48px footprint as the picker, so resolving the list doesn't shift the layout.
            // A skeleton (rather than a modal-wide blocker) keeps the amount, plan and
            // authorization readable while the short fetch completes; Confirm stays disabled.
            <div
              role="status"
              aria-busy="true"
              aria-label={t('payments.autopay.modal.loadingAccounts')}
              className="h-12 w-full animate-pulse rounded-md border border-input bg-muted"
            />
          ) : hasAccounts ? (
            <PickerPaymentAccount
              items={accounts}
              value={resolvedId}
              onChange={setSelId}
              label={t('payments.autopay.modal.accountLabel')}
              onAddNew={onAddAccount}
              addNewLabel={t('payments.autopay.modal.addAccount')}
              defaultLabel={t('payments.autopay.modal.defaultBadge')}
            />
          ) : (
            <div className="rounded-lg border border-dashed p-5 text-center">
              <div className="mx-auto mb-2.5 flex h-10 w-10 items-center justify-center rounded-full border bg-muted text-foreground">
                <Icon name="creditCard" />
              </div>
              <div className="text-sm font-semibold text-foreground">{t('payments.autopay.modal.noAccountsTitle')}</div>
              <p className="mb-3 mt-1 text-xs text-muted-foreground">{t('payments.autopay.modal.noAccountsBody')}</p>
              {onAddAccount && (
                <Button type="button" size="sm" className="mx-auto" onClick={onAddAccount}>
                  <Icon name="plus" weight="solid" size="sm" /> {t('payments.autopay.modal.addAccountButton')}
                </Button>
              )}
            </div>
          )}
        </div>

        <Label
          className={cn(
            'flex w-full cursor-pointer items-start gap-2.5 rounded-lg border p-3 text-left font-normal transition-colors',
            touched && !auth
              ? 'border-destructive bg-destructive/5'
              : auth
                ? 'border-primary/40 bg-accent'
                : 'hover:bg-accent',
          )}
        >
          <Checkbox
            checked={auth}
            onCheckedChange={(checked) => {
              setAuth(checked === true);
              setTouched(false);
            }}
            aria-invalid={(touched && !auth) || undefined}
            className="mt-0.5"
          />
          <span className="text-sm leading-relaxed text-muted-foreground">
            {t('payments.autopay.modal.authorization')}
          </span>
        </Label>
        {touched && !auth && (
          <p className="mt-1.5 flex items-start gap-1.5 text-xs text-destructive">
            <Icon name="alertCircle" weight="solid" size="xs" className="mt-px" />
            {t('payments.autopay.modal.authRequired')}
          </p>
        )}

        <div className="mt-4 flex items-start gap-3 rounded-xl bg-success/10 p-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success/15 text-success">
            <Icon name="shieldCheck" weight="solid" size="lg" />
          </span>
          <div>
            <div className="text-sm font-semibold text-success">{t('payments.autopay.modal.whyTitle')}</div>
            <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">
              {t('payments.autopay.modal.whyBody')}
            </p>
          </div>
        </div>
      </ResponsiveModalBody>

      <ResponsiveModalFooter
        note={
          <>
            <Icon name="lock" weight="solid" size="xs" /> {t('payments.autopay.modal.secureNote')}
          </>
        }
      >
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t('payments.autopay.modal.cancel')}
        </Button>
        <Button type="button" onClick={handleConfirm} disabled={isPending || isLoadingCards}>
          {isPending ? (
            <>
              <Icon name="spinner" weight="solid" size="sm" className="animate-spin" />{' '}
              {t('payments.autopay.modal.processing')}
            </>
          ) : (
            <>
              <Icon name="lock" weight="solid" size="xs" /> {t('payments.autopay.modal.confirm')}
            </>
          )}
        </Button>
      </ResponsiveModalFooter>
    </ResponsiveModal>
  );
}
